import { Api } from './Api';
import { ApiRequestOptions } from './ApiRequestOptions';
import { createSearchParams } from 'react-router-dom';
import { FetchData } from './FetchData';
import { GetEndpointMethodNames } from './GetEndpointMethodNames';
import { GetMethod } from './GetMethod';

/**
 * Wrapper for fetchData
 *
 * @param {TPath} path - API endpoint path
 * @param {TMethod} method - fetch method
 * @param {ApiRequestOptions<TPath, TMethod>} [options={}] - optional parameter to include data like body, headers, query and path parameters etc.
 *
 * @returns {Promise<GetMethod<TPath, TMethod>["Response"]>} Promise
 */
export const apiFetch = <
  TApi extends Api<any>,
  TPath extends TApi['Path'],
  TMethodName extends GetEndpointMethodNames<TApi, TPath>,
>(
  path: TPath,
  method: TMethodName,
  options: ApiRequestOptions<TApi, TPath, TMethodName> = {},
  fetchData: FetchData,
): Promise<GetMethod<TApi, TPath, TMethodName>['Response']> => {
  const {
    body,
    formData,
    queryParameters = {},
    pathParameters = {},
    headers,
    ...restOptions
  } = options;

  const searchParams = createSearchParams(queryParameters).toString();
  const pathParametersEntries = Object.entries(pathParameters);
  // parse path to replace url parameters to the value getted
  let pathParsed: string = String(path);
  pathParametersEntries.forEach(([key, value]) => {
    pathParsed = pathParsed.replaceAll(`{${key}}`, `${value}`);
  });

  const href: string =
    '/' + pathParsed + (searchParams.length > 0 ? '?' + searchParams : '');

  return fetchData<GetMethod<TApi, TPath, TMethodName>['Response']>(href, {
    method: method as string,
    body: formData || (body ? JSON.stringify(body) : undefined),
    ...restOptions,
    headers,
  });
};
